import { useEffect, useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Grid,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import { useDispatch } from "react-redux";

import { store } from "@store/store";
import { Condition } from "@interfaces/application";
import { ContractualRevision } from "@interfaces/revision";
import { setDialogConfig } from "@store/slices/componentsSlice";
import { RevisionCheckPointDialogProps } from "@interfaces/slices";
import { useLazyGetApplicationQuery, useLazyGetConditionsQuery } from "@services/api/applications";
import { useLazyGetContractualRevisionQuery } from "@services/api/revision";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatCurrency } from "@helpers/formats";

export const PuntoRevision = (props: RevisionCheckPointDialogProps) => {
    const dispatch = useDispatch();
    const hasFetched = useRef(false);

    const [openingFee, setOpeningFee] = useState<number>(0);
    const [currentCondition, setCurrenCondition] = useState<Condition | undefined>(undefined);
    const [result, setResult] = useState<ContractualRevision>({
        data: {
            documents: [],
            folder_name: "",
            tennant: "",
            singer_provider: "",
        },
        message: "",
        success: true,
    });

    const [triggerGetApplication] = useLazyGetApplicationQuery();
    const [triggerGetCurrentCondition] = useLazyGetConditionsQuery();
    const [triggerGetRevisionContractuales, { isFetching: isFetchingGetRevisionContractuals }] =
        useLazyGetContractualRevisionQuery();

    const applicationSelected = (state = store.getState()) => state.app.applicationSelected;

    const loadInfo = async () => {
        try {
            if (["CSEVD", "CSVDE"].includes(props.checkPointType)) {
                const result = await triggerGetRevisionContractuales(applicationSelected(store.getState()).id).unwrap();
                setResult(result);

                const appInfo = await triggerGetApplication(applicationSelected(store.getState()).id, false).unwrap();
                setOpeningFee(appInfo.opening_fee);

                const searchParams = new URLSearchParams([["q", "active:true"]]);
                const activeCondition = await triggerGetCurrentCondition({
                    applicationId: applicationSelected(store.getState()).id ?? "0-0-0-0-0",
                    params: searchParams.toString(),
                }).unwrap();

                setCurrenCondition(activeCondition.at(0));
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (hasFetched.current) return;
        loadInfo();

        hasFetched.current = true;
    }, []);

    return (
        <Box margin={4} py={2} maxHeight={"60vh"} minWidth={"250px"}>
            <Stack rowGap={2}>
                <Typography variant="body1" color="main3" fontWeight={600} textAlign={"center"}>
                    Checkpoint {props.title}
                </Typography>
                {currentCondition !== undefined && (
                    <Grid container>
                        <Grid size={12}>
                            <Typography variant="h5" fontWeight={500}>
                                Condiciones de crédito
                            </Typography>
                        </Grid>

                        <Grid size={3}>
                            <Typography>Monto Seleccionado</Typography>
                        </Grid>
                        <Grid size={3}>
                            <Typography>{formatCurrency(currentCondition.selected_amount, "$", 0)}</Typography>
                        </Grid>
                        <Grid size={3}>
                            <Typography>Monto Máximo Autorizado</Typography>
                        </Grid>
                        <Grid size={3}>
                            <Typography>{formatCurrency(currentCondition.approved_amount, "$", 0)}</Typography>
                        </Grid>

                        <Grid size={3}>
                            <Typography>Plazo seleccionado</Typography>
                        </Grid>
                        <Grid size={3}>
                            <Typography>{currentCondition.selected_term}</Typography>
                        </Grid>
                        <Grid size={3}>
                            <Typography>Plazo autorizado</Typography>
                        </Grid>
                        <Grid size={3}>
                            <Typography>
                                Min: {currentCondition.approved_min_term} - Max: {currentCondition.approved_max_term}
                            </Typography>
                        </Grid>
                        <Grid size={3}>
                            <Typography>Tasa autorizada</Typography>
                        </Grid>
                        <Grid size={3}>
                            <Typography>{currentCondition.interest_rate}%</Typography>
                        </Grid>
                        <Grid size={6}></Grid>
                        <Grid size={3}>
                            <Typography>Comisión por apertura</Typography>
                        </Grid>
                        <Grid size={3}>
                            <Typography>{openingFee}%</Typography>
                        </Grid>
                        <Grid size={6}></Grid>
                    </Grid>
                )}
                {isFetchingGetRevisionContractuals ? (
                    <Accordion expanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Skeleton variant="text" width={"100%"} />
                        </AccordionSummary>
                        <AccordionDetails>
                            <Skeleton variant="text" />
                            <Skeleton variant="text" />
                        </AccordionDetails>
                    </Accordion>
                ) : (
                    <>
                        {result?.success === false && (
                            <Alert variant="filled" severity="warning">
                                {result?.message}
                            </Alert>
                        )}
                        {Array.isArray(result?.data) === false &&
                            result?.data.documents.map((document, idx) => {
                                return (
                                    <Accordion key={idx}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="body1" color="main3" fontWeight={600}>
                                                {document.file_name}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body1" color="main3" fontWeight={300}>
                                                Firmantes:
                                            </Typography>
                                            <TableContainer variant="emphasis">
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Nombre</TableCell>
                                                            <TableCell>Correo</TableCell>
                                                            <TableCell>Teléfono</TableCell>
                                                            <TableCell>RFC</TableCell>
                                                            <TableCell>CURP</TableCell>
                                                            <TableCell>Tipo</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {document.signers.map((signer, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{signer.name}</TableCell>
                                                                <TableCell>{signer.email}</TableCell>
                                                                <TableCell>{signer.phone}</TableCell>
                                                                <TableCell>{signer.associates.rfc}</TableCell>
                                                                <TableCell>{signer.associates.curp}</TableCell>
                                                                <TableCell>
                                                                    {signer.type === "external"
                                                                        ? `Externo`
                                                                        : `Interno - ${signer.role}`}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                    </>
                )}
            </Stack>
            <Stack direction={"row"} justifyContent={"space-around"} mt={3} pb={2}>
                <Button variant="blue_outlined" onClick={() => dispatch(setDialogConfig())}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    type="submit"
                    sx={{ maxWidth: "250px" }}
                    onClick={props.onAcceptClick}
                    disabled={result?.success === false}
                >
                    {props.checkPointType === "CSEVD" ? "Continuar con la liberación para Revisión Ext" : ""}
                    {props.checkPointType === "CSVDE" ? "Continuar con la liberación para firma" : ""}
                </Button>
            </Stack>
        </Box>
    );
};
